/**
 * Last visible change of each indexable marketing URL, as YYYY-MM-DD.
 *
 * Pacific calendar day of the last commit that changed copy, a fact, a
 * price, or a source for that URL. A UTC or IST timestamp that falls on the
 * previous evening in America/Los_Angeles keeps that Pacific day. A later
 * commit that only rewired canonical hosts or a layout class does not move
 * the date. Bump a path only for that kind of change. The sitemap must not
 * call `new Date()`.
 *
 * The same map is copied in `backend/src/services/seoPageDates.ts`. The
 * backend test reads this file and fails if the two diverge.
 */

export const PAGE_UPDATED: Record<string, string> = {
    "/": "2026-10-27",
    "/ai-chat-widget": "2026-10-27",
    "/ai-knowledge-base": "2026-10-11",
    "/ai-workflow-builder": "2026-05-11",
    "/ai-voice-agents": "2026-10-27",
    "/ai-call-pipeline": "2026-10-27",
    "/ai-outbound-triggers": "2026-10-27",
    // CRM_PAGE_UPDATED_ON in CRMMarketingPage.tsx. The channel-table refresh
    // shipped 2026-11-03 Pacific.
    "/ai-crm": "2026-11-03",
    "/sales-call-pipeline": "2026-08-21",
    "/managed-support": "2026-08-17",
    "/shopify-ai-customer-support": "2026-10-27",
    "/docs": "2026-09-03",
    "/docs/shopify": "2026-10-08",
    "/use-cases": "2026-10-27",
    "/case-studies": "2026-06-05",
    "/compare": "2026-10-27",
    "/alternatives": "2026-06-10",
    "/pricing": "2026-10-18",
    "/blogs": "2026-10-27",
    "/contact": "2026-10-26",
    "/privacy": "2026-10-27",
    "/terms": "2026-10-26",
    "/signup": "2026-11-04",
    "/cookies": "2026-10-11",
    "/support/cloudbeds-integration": "2026-09-05",
    "/support/mews-integration": "2026-09-05",
    "/blog/how-to-set-up-ai-voice-agent-inbound-calls": "2026-10-27",
    "/tools/missed-call-calculator": "2026-11-03",

    "/use-cases/auto-dealers": "2026-05-09",
    "/use-cases/dental-healthcare": "2026-05-09",
    "/use-cases/education": "2026-10-22",
    "/use-cases/fitness-wellness": "2026-05-09",
    "/use-cases/general-support": "2026-10-27",
    "/use-cases/home-services": "2026-10-27",
    "/use-cases/hospitality": "2026-10-27",
    "/use-cases/insurance": "2026-10-27",
    "/use-cases/legal": "2026-05-09",
    "/use-cases/logistics": "2026-10-27",
    "/use-cases/real-estate": "2026-05-09",
    "/use-cases/restaurants": "2026-10-27",
    "/use-cases/retail": "2026-10-27",

    "/case-studies/outreachbox": "2026-06-05",
    "/case-studies/pulsereal": "2026-06-05",
    "/case-studies/inclusivespeak": "2026-06-05",

    "/compare/flogpt-vs-vapi": "2026-10-27",
    "/compare/flogpt-vs-bland": "2026-10-09",
    "/compare/flogpt-vs-retell": "2026-06-10",
    "/compare/flogpt-vs-synthflow": "2026-10-27",

    "/docs/get-started/start-here": "2026-09-03",
    "/docs/get-started/dashboard": "2026-09-03",
    "/docs/get-started/insights": "2026-09-03",
    "/docs/get-started/ai-calls": "2026-08-31",
    "/docs/get-started/call-centre": "2026-09-03",
    "/docs/get-started/chat": "2026-09-03",
    "/docs/get-started/knowledge": "2026-08-31",
    "/docs/get-started/support": "2026-09-03",
    "/docs/get-started/audience": "2026-09-03",
    "/docs/get-started/integrations": "2026-10-09",
    "/docs/get-started/setup": "2026-10-05",

    "/docs/manual/start-here": "2026-09-03",
    "/docs/manual/dashboard": "2026-09-03",
    "/docs/manual/insights": "2026-10-09",
    "/docs/manual/ai-calls": "2026-10-09",
    "/docs/manual/call-centre": "2026-11-03",
    "/docs/manual/chat": "2026-09-02",
    "/docs/manual/knowledge": "2026-09-16",
    "/docs/manual/support": "2026-09-16",
    "/docs/manual/audience": "2026-09-03",
    "/docs/manual/integrations": "2026-10-09",
    "/docs/manual/setup": "2026-10-08",
};

const UPDATED_MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
] as const;

/** "October 27, 2026" from a `PAGE_UPDATED` value. Does not consult the clock. */
export function formatPageUpdated(iso: string): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match) throw new Error(`Page date must be YYYY-MM-DD, got ${iso}`);
    const month = UPDATED_MONTHS[Number(match[2]) - 1];
    if (!month) throw new Error(`Page date must be YYYY-MM-DD, got ${iso}`);
    return `${month} ${Number(match[3])}, ${match[1]}`;
}
